import {
  clearStoredSession,
  clearToken,
  purgeLegacyKeys,
  readStoredSession,
  readToken,
  writeStoredSession,
  writeToken
} from '../../lib/api/sessionStorage';

/**
 * Session slice — owns the authenticated session and tenant branding record.
 * A null session means anonymous; `sessionStatus` lets guards hold a neutral
 * screen while the stored session hydrates instead of flashing the login page.
 * Persistence goes through lib/api/sessionStorage.js with the agiOps: key
 * prefix and tenant-scope guard (D10.5).
 *
 * Stuck-session fix (auth-full-spec §5): hydrateSession no longer trusts the
 * stored record alone — a stored token is revalidated against PB
 * (auth-refresh) before the session is considered live. Until that resolves,
 * status stays HYDRATING so RequireAuth holds its neutral route-loading
 * screen; a rejected token clears the session (401) and a network failure
 * keeps it optimistically (offline tolerance).
 */

export const SESSION_STATUS = {
  HYDRATING: 'hydrating',
  AUTHENTICATED: 'authenticated',
  ANONYMOUS: 'anonymous'
};

export const AGENCY_TENANT = {
  id: 'agency-default',
  name: 'AGI Agency',
  slug: 'agency',
  logoUrl: '',
  primaryColor: '#52e6a6',
  kind: 'agency'
};

export const CLIENT_TENANT = {
  id: 'tenant-helios',
  name: 'Helios Logistics',
  slug: 'helios-logistics',
  logoUrl: '',
  primaryColor: '#0f8a5f',
  kind: 'client'
};

/**
 * Tenant branding record for a session (go-live-readiness.md §3): a real
 * claims tenantId (PB client_id — the same claim backend/middleware/auth.js
 * derives tenantId from) always wins; the constants below exist ONLY as the
 * fallback for claim-less (mock password / demo) sessions. Stored sessions
 * from earlier builds carry the constant ids, so they keep hydrating exactly
 * as before.
 */
function tenantNameFromClaims(tenant, tenantId) {
  const slug = String(tenant || tenantId || '');
  const named = slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return named || slug;
}

function tenantRecordFor(session) {
  const tenantId = session?.tenantId;
  if (tenantId === AGENCY_TENANT.id) return AGENCY_TENANT;
  if (tenantId === CLIENT_TENANT.id) return CLIENT_TENANT;
  if (tenantId) {
    // Claims tenant with no local constant — derive branding from the claims
    // rather than forcing a demo tenant onto a real one.
    const base = session.role === 'client' ? CLIENT_TENANT : AGENCY_TENANT;
    return {
      ...base,
      id: tenantId,
      name: tenantNameFromClaims(session.tenant, tenantId),
      slug: session.tenant || tenantId,
      logoUrl: ''
    };
  }
  // Claim-less (mock password flow) — role-based demo constants.
  return session?.role === 'client' ? CLIENT_TENANT : AGENCY_TENANT;
}

/**
 * Revalidate the stored token against PocketBase (auth-refresh). Resolves
 * { ok: true, record? } — record carries the FRESH claims when PB answered.
 * Any non-OK status (expired/revoked/invalid token) resolves { ok: false };
 * only a network-level failure rejects (offline tolerance at the caller).
 * Kept lib-local (plain fetch) so the store never imports the API layer.
 */
async function pbAuthRefresh(token) {
  try {
    const response = await fetch('/api/collections/users/auth-refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: '{}'
    });
    if (!response.ok) return { ok: false };
    const data = await response.json().catch(() => ({}));
    return { ok: true, record: data?.record || null };
  } catch {
    // Network-level failure (offline, PB down) — distinguishable from a
    // rejected token so hydration can keep the session optimistically.
    throw new Error('network');
  }
}

export const createSessionSlice = (set) => ({
  session: null,
  sessionStatus: SESSION_STATUS.HYDRATING,
  tenantRecord: AGENCY_TENANT,

  // Restore the persisted session (localStorage, see lib/api/sessionStorage.js).
  // Called once from main.jsx before the router mounts; async since the §5
  // token-revalidation fix — the router does NOT block on it: guards hold the
  // neutral HYDRATING screen while the auth-refresh probe is in flight, then
  // status settles in exactly one place per branch below.
  hydrateSession: () => {
    // No active tenant yet at hydration time — the tenant-scope check inside
    // readStoredSession() tolerates a missing tenantId (D10.5). purgeLegacyKeys
    // drops pre-D10.5 unprefixed keys from older builds.
    purgeLegacyKeys();
    const stored = readStoredSession();
    if (stored && stored.user && stored.role) {
      const token = readToken();
      if (!token) {
        // Session-shaped record with no token can never authenticate — the
        // stuck-session shape (§5). Clear, don't trust.
        clearStoredSession();
        set({
          session: null,
          sessionStatus: SESSION_STATUS.ANONYMOUS,
          tenantRecord: AGENCY_TENANT
        });
        return;
      }
      // Optimistic-authenticated is NOT set yet: revalidate first so a stale
      // token never renders a session that dies on its next API call.
      pbAuthRefresh(token)
        .then(({ ok, record }) => {
          if (!ok) {
            clearToken();
            clearStoredSession();
            set({
              session: null,
              sessionStatus: SESSION_STATUS.ANONYMOUS,
              tenantRecord: AGENCY_TENANT
            });
            return;
          }
          // Fresh claims win where they differ (role/client_id rotations).
          const fresh = record
            ? {
                ...stored,
                role: record.role || stored.role,
                tenantId: record.client_id || stored.tenantId
              }
            : stored;
          writeStoredSession(fresh, fresh?.tenantId);
          set({
            session: fresh,
            sessionStatus: SESSION_STATUS.AUTHENTICATED,
            tenantRecord: tenantRecordFor(fresh)
          });
        })
        .catch(() => {
          // Network failure — keep the session optimistically (offline
          // tolerance); the 401 interceptor in lib/api/http.js covers the
          // case where the token turns out dead once traffic resumes.
          set({
            session: stored,
            sessionStatus: SESSION_STATUS.AUTHENTICATED,
            tenantRecord: tenantRecordFor(stored)
          });
        });
      return;
    }
    set({
      session: null,
      sessionStatus: SESSION_STATUS.ANONYMOUS,
      tenantRecord: AGENCY_TENANT
    });
  },

  applySession: ({ token, session }) => {
    writeToken(token);
    writeStoredSession(session, session?.tenantId);
    set({
      session,
      sessionStatus: SESSION_STATUS.AUTHENTICATED,
      tenantRecord: tenantRecordFor(session)
    });
  },

  clearSession: () => {
    clearToken();
    clearStoredSession();
    set({
      session: null,
      sessionStatus: SESSION_STATUS.ANONYMOUS,
      tenantRecord: AGENCY_TENANT
    });
  }
});
